import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button, Result, Spin, theme } from "antd";

import { ssoCallback } from "./api";
import { useAuthStore } from "@/stores/authStore";
import { apiErrorMessage } from "@/lib/errors";

/** What a sign-in the identity provider itself refused reads. */
export const SSO_REFUSED_BY_PROVIDER =
  "Your company sign-in was cancelled or refused. Try again from the sign-in page.";

/**
 * Where the identity provider sends the browser back (SASE edition, SASE-30):
 * `/login/sso?code=…&state=…`. The page hands both to the backend once, which
 * checks them and answers with a session like a password sign-in's.
 */
function SsoCallbackPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const setSession = useAuthStore((s) => s.setSession);
  const { token } = theme.useToken();
  const [error, setError] = useState<string | null>(null);
  // The state is single-use: never send it twice (React re-runs effects in development).
  const sent = useRef(false);

  useEffect(() => {
    if (sent.current) return;
    sent.current = true;
    const code = params.get("code");
    const state = params.get("state");
    if (params.get("error") || !code || !state) {
      setError(SSO_REFUSED_BY_PROVIDER);
      return;
    }
    ssoCallback(code, state)
      .then((response) => {
        setSession({
          accessToken: response.access_token,
          expiresIn: response.expires_in,
          user: response.user,
        });
        navigate("/", { replace: true });
      })
      .catch((err: unknown) => setError(apiErrorMessage(err, SSO_REFUSED_BY_PROVIDER)));
  }, [params, navigate, setSession]);

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: token.colorBgLayout,
      }}
    >
      {error ? (
        <Result
          status="warning"
          title="You are not signed in"
          subTitle={error}
          extra={
            <Link to="/login">
              <Button type="primary">Back to sign-in</Button>
            </Link>
          }
        />
      ) : (
        <Spin size="large" tip="Signing you in…">
          <div style={{ padding: 48 }} />
        </Spin>
      )}
    </div>
  );
}

export default SsoCallbackPage;
